import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { ApiError, get, put } from '../api'
import type { BlockedReason, Homework as Brief, Submission } from '../api'
import { BlockView } from '../components/Blocks'
import type { Block } from '../components/Blocks'
import { useT } from '../i18n'
import common from '../i18n/common'
import learn from '../i18n/learn'
import { Icon, messageOf } from '../lib/ui'
import { reasonKey } from './Dashboard'
import LessonHomework from './lesson/LessonHomework'
import LessonPractice from './lesson/LessonPractice'
import '../styles/learn.css'

type Lecture = {
  id: string
  title: string
  course_id: string
  course_title: string
  section_title: string
  gate: {
    require_media: boolean
    require_homework_submission: boolean
    require_homework_acceptance: boolean
    /** Whether every practice of the lesson has to be passed; absent from an older server. */
    require_practice?: boolean
    /** How many practices the lesson has, and how many of them this learner has passed. */
    practice_total?: number
    practice_passed?: number
  }
  blocks: Block[]
  homework?: Brief
  feedback?: { sentiment: 'up' | 'down'; comment?: string }
  prev_lecture_id: string | null
  next_lecture_id: string | null
  completed?: boolean
  seeking_prevented?: boolean
}

export function Lesson() {
  const t = useT(learn)
  const tc = useT(common)
  const { lectureId = '' } = useParams()
  const navigate = useNavigate()

  const [lecture, setLecture] = useState<Lecture | null>(null)
  const [shut, setShut] = useState<BlockedReason | null>(null)
  const [failure, setFailure] = useState<{ error: unknown } | null>(null)
  // The lesson as a whole is finished (the server said so), and which of its
  // timed media blocks are — as the lesson was read (watched to the end on an
  // earlier visit), or since (the player heard the server say so). Media done
  // is not the lesson done when the lesson also asks for its practice.
  const [completed, setCompleted] = useState(false)
  const [mediaDone, setMediaDone] = useState<ReadonlySet<string>>(() => new Set())
  const [practice, setPractice] = useState({ passed: 0, total: 0 })
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    const ac = new AbortController()
    setLecture(null)
    setShut(null)
    setFailure(null)
    get<Lecture>(`/learn/lectures/${lectureId}`, ac.signal)
      .then((l) => {
        setLecture(l)
        setCompleted(Boolean(l.completed))
        setMediaDone(finishedMedia(l.blocks))
        setPractice({ passed: l.gate.practice_passed ?? 0, total: l.gate.practice_total ?? 0 })
      })
      .catch((err) => {
        if (ac.signal.aborted) return
        // A shut lecture is not a failure to report — it is the answer, and the
        // reason is what the learner needs to see.
        if (err instanceof ApiError && err.blockedReason) setShut(err.blockedReason as BlockedReason)
        else setFailure({ error: err })
      })
    return () => ac.abort()
  }, [lectureId, attempt])

  const onMediaCompleted = useCallback(
    (blockId: string) => setMediaDone((done) => (done.has(blockId) ? done : new Set(done).add(blockId))),
    [],
  )
  const onPracticeProgress = useCallback(
    (passed: number, total: number) =>
      setPractice((p) => (p.passed === passed && p.total === total ? p : { passed, total })),
    [],
  )

  // Once the page has seen the last practice passed — which the lesson as read
  // did not count yet — the server is asked again what the lesson is now. The
  // pass has already been counted there (a check is graded and the lesson
  // recomputed before the answer comes), so the page follows the server's
  // word on the lesson being finished rather than its own sum. The lesson on
  // screen is left as it is: replacing it would restart the player.
  const lessonId = lecture?.id ?? null
  const requirePracticeNow = lecture?.gate.require_practice === true
  const practiceKnownPassed =
    lecture !== null &&
    (lecture.gate.practice_total ?? 0) > 0 &&
    (lecture.gate.practice_passed ?? 0) >= (lecture.gate.practice_total ?? 0)
  const practiceAllPassed = practice.total > 0 && practice.passed >= practice.total
  useEffect(() => {
    if (lessonId === null || completed || !requirePracticeNow || practiceKnownPassed || !practiceAllPassed) return
    const ac = new AbortController()
    get<Lecture>(`/learn/lectures/${lessonId}`, ac.signal)
      .then((l) => {
        if (l.completed) setCompleted(true)
        const finished = finishedMedia(l.blocks)
        setMediaDone((done) => ([...finished].every((id) => done.has(id)) ? done : new Set([...done, ...finished])))
      })
      // The page as it stands is still right as far as it goes; a reload asks again.
      .catch(() => undefined)
    return () => ac.abort()
  }, [lessonId, completed, requirePracticeNow, practiceKnownPassed, practiceAllPassed])

  if (shut) return <Shut reason={shut} />
  if (failure) {
    return (
      <div className="empty" role="alert">
        <h1>{t('lessonLoadFailed')}</h1>
        <p>
          {failure.error instanceof ApiError && failure.error.status === 404
            ? t('lessonNotFound')
            : messageOf(failure.error, learn)}
        </p>
        <div className="empty__actions">
          <button type="button" className="button" onClick={() => setAttempt((n) => n + 1)}>
            {tc('retry')}
          </button>
          <Link className="button button--quiet" to="/courses">
            {t('myCourses')}
          </Link>
        </div>
      </div>
    )
  }
  if (!lecture) return <div className="lesson lesson--loading" aria-busy="true" />

  // The gate waits on timed media only where there is some: a lesson of text
  // and handouts is finished by being opened, as the server counts it too.
  // Where there is, it waits for every one of them, as the server does.
  // Practice likewise: a lesson that asks for it but has none asks nothing.
  const media = lecture.blocks.filter(isTimedMedia)
  const requirePractice = lecture.gate.require_practice === true
  const waitingForMedia =
    !completed && lecture.gate.require_media && media.length > 0 && !media.every((b) => mediaDone.has(b.id))
  const waitingForPractice = !completed && requirePractice && practice.total > 0 && practice.passed < practice.total
  const waiting = waitingForMedia || waitingForPractice

  return (
    <article className="lesson">
      <header className="lesson__head">
        <Link className="link link--back" to={`/courses/${lecture.course_id}`}>
          <Icon name="chevronLeft" size={16} />
          {lecture.course_title}
        </Link>
        <h1>{lecture.title}</h1>
        <p className="lesson__section">{lecture.section_title}</p>
      </header>

      {lecture.blocks
        .slice()
        .sort((a, b) => a.seq - b.seq)
        .map((block) => (
          <LessonBlock
            key={block.id}
            block={block}
            seekingPrevented={lecture.seeking_prevented !== false}
            onMediaCompleted={onMediaCompleted}
          />
        ))}

      <LessonPractice
        lectureId={lecture.id}
        requirePractice={requirePractice}
        onProgress={onPracticeProgress}
      />

      {lecture.homework && (
        <LessonHomework
          lectureId={lecture.id}
          homework={lecture.homework}
          // Folding the new submission into what is already on screen beats
          // refetching the lecture: the player would remount and lose its place.
          onSubmitted={(s: Submission) =>
            setLecture((current) =>
              current && current.homework
                ? {
                    ...current,
                    homework: {
                      ...current.homework,
                      submissions: [s, ...(current.homework.submissions ?? [])],
                    },
                  }
                : current,
            )
          }
        />
      )}

      <Rating key={lecture.id} lectureId={lecture.id} current={lecture.feedback?.sentiment} />

      <nav className="lesson__nav" aria-label={t('lessonNavigation')}>
        {lecture.prev_lecture_id ? (
          <Link className="button button--quiet" to={`/lessons/${lecture.prev_lecture_id}`}>
            <Icon name="chevronLeft" size={18} />
            {t('previousLesson')}
          </Link>
        ) : (
          <span />
        )}
        {lecture.next_lecture_id && (
          <button
            type="button"
            className="button"
            // Following the link before the gate opens would only bounce the
            // learner back with a "not yet" message.
            disabled={waiting}
            onClick={() => navigate(`/lessons/${lecture.next_lecture_id}`)}
          >
            {t('nextLesson')}
            <Icon name="chevronRight" size={18} />
          </button>
        )}
      </nav>

      {lecture.next_lecture_id && waiting && (
        <p className="lesson__hint">
          {waitingForMedia && waitingForPractice
            ? t('nextAfterVideoAndPractice', { passed: practice.passed, total: practice.total })
            : waitingForPractice
              ? t('nextAfterPractice', { passed: practice.passed, total: practice.total })
              : t('nextAfterVideo')}
        </p>
      )}
    </article>
  )
}

type TimedBlock = Extract<Block, { kind: 'video' | 'audio' }>

/** The blocks the lesson's gate counts as media (the server's gateable kinds): watched or listened to through. */
function isTimedMedia(b: Block): b is TimedBlock {
  return b.kind === 'video' || b.kind === 'audio'
}

/** The timed media blocks the server already counts as finished for this learner. */
function finishedMedia(blocks: Block[]): Set<string> {
  return new Set(blocks.filter(isTimedMedia).flatMap((b) => (b.progress?.completed ? [b.id] : [])))
}

/** One of the lesson's own blocks, saying which block it is when its media is finished. */
function LessonBlock({
  block,
  seekingPrevented,
  onMediaCompleted,
}: {
  block: Block
  seekingPrevented: boolean
  onMediaCompleted: (blockId: string) => void
}) {
  const id = block.id
  const onCompleted = useCallback(() => onMediaCompleted(id), [onMediaCompleted, id])
  return <BlockView block={block} seekingPrevented={seekingPrevented} onCompleted={onCompleted} />
}

function Rating({ lectureId, current }: { lectureId: string; current?: 'up' | 'down' }) {
  const t = useT(learn)
  const tc = useT(common)
  const [chosen, setChosen] = useState<'up' | 'down' | undefined>(current)
  const [saving, setSaving] = useState(false)
  // What the last press came to, said once beside the buttons.
  const [said, setSaid] = useState<'thanks' | 'failed' | null>(null)

  async function rate(sentiment: 'up' | 'down') {
    if (saving) return
    setSaving(true)
    setSaid(null)
    const previous = chosen
    setChosen(sentiment) // optimistic: the control should answer immediately
    try {
      await put(`/learn/lectures/${lectureId}/feedback`, { sentiment, comment: '' })
      setSaid('thanks')
    } catch {
      setChosen(previous)
      setSaid('failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="rating" aria-labelledby={`rating-${lectureId}`}>
      <span id={`rating-${lectureId}`}>{t('ratingQuestion')}</span>
      <button
        type="button"
        className={chosen === 'up' ? 'chip chip--on' : 'chip'}
        aria-pressed={chosen === 'up'}
        onClick={() => void rate('up')}
      >
        {tc('yes')}
      </button>
      <button
        type="button"
        className={chosen === 'down' ? 'chip chip--on' : 'chip'}
        aria-pressed={chosen === 'down'}
        onClick={() => void rate('down')}
      >
        {tc('no')}
      </button>
      <span className="rating__status" role="status" aria-live="polite">
        {said === 'failed' ? t('ratingFailed') : said === 'thanks' ? t('ratingThanks') : ''}
      </span>
    </section>
  )
}

function Shut({ reason }: { reason: BlockedReason }) {
  const t = useT(learn)
  return (
    <div className="empty">
      <h1>{t('lessonShut')}</h1>
      <p>{t(reasonKey(reason))}</p>
      <Link className="button" to="/courses">
        {t('myCourses')}
      </Link>
    </div>
  )
}
